/** What each role permits, in words — one home for the People screen and a person's own page.
 *
 * Moved out of `People.tsx` when a person got a page of their own: a role's description written in
 * two files is two descriptions, and the one that drifts from `require_roles` is how somebody grants
 * more than they meant to.
 */

import type { Member, MemberRole } from "../api/queries";
import { shortDate } from "../lib/format";

/** What each role actually permits, in the terms the product enforces them in.
 *
 * Written from `require_roles` rather than from intuition: a description that drifts from the
 * check is how somebody grants more than they meant to.
 */
export const ROLES: ReadonlyArray<{ value: MemberRole; label: string; can: string }> = [
  {
    value: "admin",
    label: "Admin",
    can: "everything an Approver can, plus administering people and roles",
  },
  {
    value: "approver",
    label: "Approver",
    can: "accepts figures into the inventory, decides approvals and issues filings. Needs a second factor",
  },
  {
    value: "contributor",
    label: "Contributor",
    can: "uploads evidence, corrects extractions and prepares figures — does not accept them",
  },
  { value: "viewer", label: "Viewer", can: "reads the inventory and reports, changes nothing" },
  {
    value: "auditor",
    label: "Auditor",
    can: "reads reports and evidence for the periods their engagement covers, and nothing outside them",
  },
];

export const CAN: Record<string, string> = Object.fromEntries(ROLES.map((r) => [r.value, r.can]));

export const LABEL: Record<string, string> = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));

/** The window over the data an Auditor's membership is limited to, as the API carries it.
 *
 * `period_scope` is `{from, to}` as ISO dates, or absent. Read defensively: it is a JSON column and
 * a value of another shape is not a period this screen can name, so it is treated as none — and an
 * Auditor with none is shown the warning, which is the conservative reading. */
export function periodOf(raw: Member["period_scope"]): { from: string; to: string } | null {
  if (!raw || typeof raw !== "object") return null;
  const { from, to } = raw as { from?: unknown; to?: unknown };
  return typeof from === "string" && typeof to === "string" ? { from, to } : null;
}

/** A filing in the words an Admin recognises it by: the company, the regime, the year. */
export function filingLabel(filing: {
  legal_entity_name: string;
  regime_name: string;
  financial_year_end: string;
}): string {
  return `${filing.legal_entity_name} · ${filing.regime_name} · year ending ${shortDate(filing.financial_year_end)}`;
}
